import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Alert, Box, Button, Chip, CircularProgress, Grid, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axiosInstance from '@/services/api/axios';
import { useSelector } from 'react-redux';
import { canAccessPage, PageKey } from '@/auth/accessMatrix';
import { RootState } from '@/store';
import CurrencyAmounts, { type CurrencyAmount } from '@/components/CurrencyAmounts';

type DirectionStats = {
  amounts_by_currency?: CurrencyAmount[];
  total: number; success: number; declined: number; accepted: number; appeal: number; not_found: number;
  turnover: string | number; conversion: number; not_found_conversion: number;
};

type DashboardData = {
  trading_traders: number;
  period: { from_date: string; to_date: string };
  generated_at: string;
  overall_status: 'NORMAL' | 'DEGRADED' | 'CRITICAL';
  attention_count: number;
  transactions: Record<'IN' | 'OUT', DirectionStats>;
  top_not_found_amounts?: Array<{
    amount: string | number; currency: string | null;
    not_found_count: number; total_count: number; not_found_percent: number;
  }>;
  stuck_transactions: number;
  stuck_payouts: number;
  support_api_available: boolean;
  appeals: null | { open_now: number; overdue: number; oldest_age_minutes: number; total_amount: string | number };
  payouts: { by_status: Record<string, number>; requires_attention?: number; oldest_pending_at: string | null };
  settlements: { accepted: number; final_amount_usdt: string | number };
  operations: {
    active_traders: number;
    active_requisites: number;
    traders_without_requisites: number;
    requisites_near_limit: number;
    requisites_near_limit_details?: Array<{
      id: string; name: string; number: string;
      counter_used: number; counter_limit: number; counter_percent: number | null;
      amount_used: string | number; amount_limit: string | number; amount_percent: number | null;
      max_percent: number;
    }>;
  };
  merchant_liquidity: Array<{ currency: string; balance: string; blocked_in: string; blocked_out: string }>;
  trader_liquidity: Array<{ currency: string; available: string; blocked_in: string; blocked_out: string }>;
};

const statusColor = (status: DashboardData['overall_status']) =>
  status === 'NORMAL' ? 'success' : status === 'CRITICAL' ? 'error' : 'warning';

const formatNumber = (value: string | number | null | undefined) =>
  Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 });

export default function Dashboard() {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fromDate, setFromDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));

  const load = useCallback(async (from?: string, to?: string) => {
    try {
      setLoading(true);
      setError('');
      setData((await axiosInstance.get<DashboardData>('/dashboard/summary', { params: from && to ? { from_date: from, to_date: to } : {} })).data);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Не удалось загрузить состояние системы');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (loading && !data) return <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>;
  if (!data) return <Alert severity="error">{error || 'Данные недоступны'}</Alert>;

  const attentionCards = [
    { title: 'Апелляции', value: data.appeals?.open_now ?? '—', detail: `Старше SLA 2 часа: ${data.appeals?.overdue ?? '—'}`, danger: Boolean(data.appeals?.overdue), path: '/appeals', page: PageKey.APPEALS },
    { title: 'Зависшие PayIn', value: data.stuck_transactions, detail: 'Только IN в ACCEPTED более 30 минут', danger: data.stuck_transactions > 0, path: '/transactions', page: PageKey.TRANSACTIONS },
    { title: 'Зависшие Payout', value: data.stuck_payouts, detail: 'ACCEPTED после expire_at', danger: data.stuck_payouts > 0, path: '/payouts-out', page: PageKey.PAYOUTS_OUT },
    {
      title: 'Выплаты требуют внимания',
      value: data.payouts.requires_attention ?? ((data.payouts.by_status.UNDER_REVIEW || 0) + (data.payouts.by_status.IN_PROGRESS || 0)),
      detail: `UNDER_REVIEW: ${data.payouts.by_status.UNDER_REVIEW || 0} · IN_PROGRESS: ${data.payouts.by_status.IN_PROGRESS || 0}`,
      danger: Boolean((data.payouts.by_status.UNDER_REVIEW || 0) + (data.payouts.by_status.IN_PROGRESS || 0)),
      path: '/payouts-out',
      page: PageKey.PAYOUTS_OUT,
    },
  ];
  const operationalCards = [
    { title: 'Settlements', value: data.settlements.accepted, detail: `${formatNumber(data.settlements.final_amount_usdt)} USDT`, danger: false, path: '/settlements', page: PageKey.SETTLEMENTS },
    { title: 'Активные трейдеры без активных реквизитов', value: data.operations.traders_without_requisites, detail: `Всего активных трейдеров: ${data.operations.active_traders}`, danger: data.operations.traders_without_requisites > 0, path: '/traders', page: PageKey.TRADERS },
    { title: 'Реквизиты около лимита', value: data.operations.requisites_near_limit, detail: `Активных: ${data.operations.active_requisites}`, danger: data.operations.requisites_near_limit > 0, path: '/requisites', page: PageKey.REQUISITES },
  ];
  const nearLimitDetails = data.operations.requisites_near_limit_details || [];

  return <Box>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
      <Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="h4" fontWeight={700}>Состояние SPRUT</Typography>
          <Chip label={data.overall_status} color={statusColor(data.overall_status)} />
        </Stack>
        <Typography color="text.secondary" mt={0.5}>
          Требует внимания: {data.attention_count} · Обновлено {new Date(data.generated_at).toLocaleString()}
        </Typography>
      </Box>
      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load(fromDate, toDate)} disabled={loading}>Обновить</Button>
    </Stack>

    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {!data.support_api_available && <Alert severity="warning" sx={{ mb: 2 }}>Support API недоступен: статистика апелляций временно не обновляется.</Alert>}

    {nearLimitDetails.length > 0 && <Paper sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h6" fontWeight={700} mb={1.5}>Реквизиты, использованные на 90% и более</Typography>
      <Stack spacing={1}>
        {nearLimitDetails.map(item => (
          <Box key={item.id} display="grid" gridTemplateColumns={{ xs: '1fr', md: 'minmax(180px, 1fr) minmax(180px, 1fr) 160px 190px' }} gap={1} alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Typography fontWeight={600}>{item.name}</Typography>
            <Typography color="text.secondary">{item.number}</Typography>
            <Typography>{item.counter_limit > 0 ? `Заявки: ${item.counter_used} / ${item.counter_limit}` : 'Лимит заявок не задан'}</Typography>
            <Typography>{Number(item.amount_limit) > 0 ? `Сумма: ${formatNumber(item.amount_used)} / ${formatNumber(item.amount_limit)}` : 'Лимит суммы не задан'}</Typography>
          </Box>
        ))}
      </Stack>
    </Paper>}

    <Stack spacing={2.5} mb={3}>
      <Box>
        <Typography variant="subtitle2" color="text.secondary" fontWeight={700} mb={1}>Требуют внимания</Typography>
        <Grid container spacing={2}>
          {attentionCards.map(card => {
            const canNavigate = canAccessPage(currentUser, card.page);
            return <Grid item xs={12} sm={6} lg={3} key={card.title}>
            <Paper onClick={canNavigate ? () => navigate(card.path) : undefined} sx={{ p: 2.25, minHeight: 126, cursor: canNavigate ? 'pointer' : 'default', border: 1, borderColor: card.danger ? 'error.main' : 'divider', '&:hover': canNavigate ? { borderColor: 'primary.main' } : undefined }}>
              <Typography color="text.secondary">{card.title}</Typography>
              <Typography variant="h4" fontWeight={700} color={card.danger ? 'error.main' : 'text.primary'} my={0.5}>{card.value}</Typography>
              <Typography variant="body2" color="text.secondary">{card.detail}</Typography>
            </Paper>
          </Grid>;
          })}
        </Grid>
      </Box>
      <Box>
        <Typography variant="subtitle2" color="text.secondary" fontWeight={700} mb={1}>Операционные показатели</Typography>
        <Grid container spacing={2}>
          {operationalCards.map(card => {
            const canNavigate = canAccessPage(currentUser, card.page);
            return <Grid item xs={12} sm={6} lg={4} key={card.title}>
            <Paper onClick={canNavigate ? () => navigate(card.path) : undefined} sx={{ p: 2.25, minHeight: 126, cursor: canNavigate ? 'pointer' : 'default', border: 1, borderColor: card.danger ? 'error.main' : 'divider', '&:hover': canNavigate ? { borderColor: 'primary.main' } : undefined }}>
              <Typography color="text.secondary">{card.title}</Typography>
              <Typography variant="h4" fontWeight={700} color={card.danger ? 'error.main' : 'text.primary'} my={0.5}>{card.value}</Typography>
              <Typography variant="body2" color="text.secondary">{card.detail}</Typography>
            </Paper>
          </Grid>;
          })}
        </Grid>
      </Box>
    </Stack>

    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
        <TextField type="date" label="Период операций: с" value={fromDate} onChange={(event) => setFromDate(event.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" label="По" value={toDate} onChange={(event) => setToDate(event.target.value)} InputLabelProps={{ shrink: true }} />
        <Button disabled={loading || !fromDate || !toDate || fromDate > toDate} onClick={() => void load(fromDate, toDate)}>Применить</Button>
        <Typography>Трейдеров с успешными операциями: <b>{data.trading_traders}</b></Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">Период UTC применяется к PayIn, PayOut, активным по операциям трейдерам и топу невыдач. Активность — по времени обновления успешной операции (если оно не сохранено — по созданию). Остальные карточки показывают текущее состояние. USDT — по текущим курсам.</Typography>
    </Paper>
    <Grid container spacing={2} mb={3}>
      {(['IN', 'OUT'] as const).map(direction => {
        const item = data.transactions[direction];
        return <Grid item xs={12} md={6} key={direction}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6" fontWeight={700}>Pay{direction === 'IN' ? 'In' : 'Out'} · {data.period?.from_date} — {data.period?.to_date}</Typography>
            <Typography variant="body2" color="text.secondary">
              Созданы за выбранный период UTC · «Всего» включает все статусы · SR = SUCCESS / всего
            </Typography>
            <Box display="grid" gridTemplateColumns={{ xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }} gap={2} mt={2}>
              {[
                ['Всего', item.total],
                ['SUCCESS', item.success],
                ['DECLINED', item.declined],
                ['NOT_FOUND', item.not_found],
                ['ACCEPTED (ожидание)', item.accepted],
                ['APPEAL (апелляция)', item.appeal ?? '—'],
                ['SR', `${item.conversion}%`],
                ['Невыдача, %', `${item.not_found_conversion}%`],
              ].map(([label, value]) => <Box key={label} sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
                <Typography variant="h6" fontWeight={700} color={label === 'SUCCESS' ? 'success.main' : 'text.primary'} noWrap>{value}</Typography>
              </Box>)}
            </Box>
            <Box mt={2}><Typography color="text.secondary">Оборот SUCCESS</Typography><CurrencyAmounts amounts={item.amounts_by_currency} /></Box>
          </Paper>
        </Grid>;
      })}
    </Grid>

    <Accordion disableGutters sx={{ mb: 3 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>Топ-10 сумм по количеству невыдач PayIn</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          За выбранный период UTC. Сортировка по количеству NOT_FOUND. Одинаковые суммы в разных валютах считаются отдельно.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>№</TableCell><TableCell>Сумма</TableCell>
              <TableCell align="right">Невыдач</TableCell>
              <TableCell align="right">Всего заявок на сумму</TableCell>
              <TableCell align="right">Невыдача, %</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {(data.top_not_found_amounts || []).map((row, index) => <TableRow key={`${row.currency}:${row.amount}`}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{formatNumber(row.amount)} {row.currency || 'Валюта неизвестна'}</TableCell>
                <TableCell align="right">{formatNumber(row.not_found_count)}</TableCell>
                <TableCell align="right">{formatNumber(row.total_count)}</TableCell>
                <TableCell align="right">{formatNumber(row.not_found_percent)}%</TableCell>
              </TableRow>)}
              {!data.top_not_found_amounts?.length && <TableRow>
                <TableCell colSpan={5} align="center">Невыдач за сегодня нет</TableCell>
              </TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>

    <Grid container spacing={2}>
      <Grid item xs={12} lg={6}>
        <Paper sx={{ p: 2 }}><Typography variant="h6" fontWeight={700} mb={1}>Ликвидность мерчантов</Typography>
          <Table size="small"><TableHead><TableRow><TableCell>Валюта</TableCell><TableCell align="right">Баланс</TableCell><TableCell align="right">Блок IN</TableCell><TableCell align="right">Блок OUT</TableCell></TableRow></TableHead>
            <TableBody>{data.merchant_liquidity.map(row => <TableRow key={row.currency}><TableCell>{row.currency}</TableCell><TableCell align="right">{formatNumber(row.balance)}</TableCell><TableCell align="right">{formatNumber(row.blocked_in)}</TableCell><TableCell align="right">{formatNumber(row.blocked_out)}</TableCell></TableRow>)}</TableBody>
          </Table></Paper>
      </Grid>
      <Grid item xs={12} lg={6}>
        <Paper sx={{ p: 2 }}><Typography variant="h6" fontWeight={700} mb={1}>Ликвидность трейдеров</Typography>
          <Table size="small"><TableHead><TableRow><TableCell>Валюта</TableCell><TableCell align="right">Доступно</TableCell><TableCell align="right">Блок IN</TableCell><TableCell align="right">Блок OUT</TableCell></TableRow></TableHead>
            <TableBody>{data.trader_liquidity.map(row => <TableRow key={row.currency}><TableCell>{row.currency}</TableCell><TableCell align="right">{formatNumber(row.available)}</TableCell><TableCell align="right">{formatNumber(row.blocked_in)}</TableCell><TableCell align="right">{formatNumber(row.blocked_out)}</TableCell></TableRow>)}</TableBody>
          </Table></Paper>
      </Grid>
    </Grid>
  </Box>;
}
